if (!customElements.get('sticky-atc-panel')) {
  /**
   * Variant ids of this product that are currently in the cart (ClickUp 869f2wye1) —
   * page-level, not per element, because the panel is replaced wholesale on every
   * variant change (data-dynamic-variant-content) and each replacement has to inherit
   * the state rather than re-derive it.
   *
   * Seeded once from the server-rendered data-in-cart attribute and owned by the cart
   * events from then on: theme.fetchCache caches the ?sections= response per URL, so a
   * later render can carry a stale attribute. Null until the first panel connects.
   */
  let inCartVariants = null;
  let cartListenersBound = false;

  /**
   * How long the panel stays up after the variant lands in the cart, so the add-to-cart
   * button's own success tick is actually seen (ClickUp 869f2wye1).
   *
   * Sized to when the cart drawer takes the screen, not to how long the tick runs:
   * product-form.js schedules the drawer render on a 700ms timeout and cart-drawer.js
   * defers open() by a further task, so from ~700ms the panel is behind the drawer and any
   * extra dwell is invisible. The @keyframes checkmark in main.css finishes drawing at 40%
   * of its 1.4s run (560ms) and holds to 90%, so 700ms shows the completed tick with the
   * hold still going.
   *
   * Follows the precedent in product-form.js, where the "After adding to cart" → page
   * branch already delays its own navigation by 300ms for the same reason.
   */
  const SUCCESS_DWELL_MS = 700;

  /**
   * Re-applies suppression to every panel on the page after a cart change.
   * @param {object} [options] - Forwarded to each panel; see applySuppression().
   */
  const refreshPanels = (options) => {
    document.querySelectorAll('sticky-atc-panel').forEach((panel) => {
      if (typeof panel.applySuppression === 'function') panel.applySuppression(options);
    });
  };

  /**
   * @param {string|number} variantId - Variant the cart change applies to.
   * @param {number} quantity - Resulting quantity; 0 (or less) removes the suppression.
   */
  const setInCart = (variantId, quantity) => {
    if (!variantId || !inCartVariants) return;
    const id = String(variantId);
    const added = Number(quantity) > 0;
    if (added) {
      inCartVariants.add(id);
    } else {
      inCartVariants.delete(id);
    }
    // Only a landing quantity earns the dwell — a removal has no success state to protect.
    // A quantity decrease that stops short of 0 also arrives as `added`, but the panel is
    // already suppressed by then, so applySuppression() short-circuits before the dwell.
    refreshPanels({ dwell: added });
  };

  const bindCartListeners = () => {
    if (cartListenersBound) return;
    cartListenersBound = true;

    // Both events bubble to document: on:cart:add from product-form.js (a first add, no
    // quantity in the detail), on:line-item:change from cart-items.js and product-form.js
    // (a quantity bump or a removal, where newQuantity 0 un-suppresses the panel).
    document.addEventListener('on:cart:add', (event) => {
      setInCart(event.detail?.variantId, 1);
    });
    document.addEventListener('on:line-item:change', (event) => {
      setInCart(event.detail?.variantId, event.detail?.newQuantity);
    });
  };

  class StickyAtcPanel extends HTMLElement {
    constructor() {
      super();

      this.productSection = this.closest('.cc-main-product');
      this.productForm = this.productSection.querySelector('product-form');

      /** Target state of an armed dwell timer; null when no commit is pending. */
      this.pendingSuppression = null;
      this.suppressTimer = null;

      if ('IntersectionObserver' in window && 'MutationObserver' in window) {
        this.bindEvents();
      }
    }

    connectedCallback() {
      // Not in the constructor: a custom element must not touch its own attributes there,
      // and the class below is an attribute write.
      if (inCartVariants === null) {
        inCartVariants = new Set(
          (this.dataset.inCart || '')
            .split(',')
            .map((id) => id.trim())
            .filter(Boolean)
        );
      }

      bindCartListeners();
      // No dwell here, deliberately: the panel is replaced wholesale on every variant
      // change, so dwelling would flash it visible for SUCCESS_DWELL_MS when switching back
      // to a variant that is already in the cart. First paint must not move either.
      this.applySuppression();
    }

    /**
     * Hides the panel while the variant it is offering is already in the cart.
     *
     * A second class alongside --out rather than a state of its own: --out belongs to the
     * IntersectionObserver in bindEvents(), which would clear anything it owns on the next
     * scroll frame.
     *
     * No data-variant-id means the configurator templates, where the panel scrolls up to
     * the configurator instead of adding to cart — having the variant in the cart says
     * nothing about whether that link is still useful.
     *
     * @param {object} [options]
     * @param {boolean} [options.dwell] - Hold the panel up for SUCCESS_DWELL_MS before
     *   hiding it, so the button's success tick is seen. Only the cart-add path passes
     *   this; connectedCallback deliberately does not (see below).
     */
    applySuppression({ dwell = false } = {}) {
      const { variantId } = this.dataset;
      if (!variantId || !inCartVariants) return;

      const suppressed = inCartVariants.has(String(variantId));

      // Compare against where the panel is *heading*, not just where it is — with a timer
      // armed, that is the timer's target. This is what stops a repeat add stacking a
      // second timer, and what lets a removal arriving mid-dwell cancel the one in flight.
      const target = this.pendingSuppression === null
        ? this.classList.contains('sticky-atc-panel--suppressed')
        : this.pendingSuppression;
      if (suppressed === target) return;

      clearTimeout(this.suppressTimer);
      this.suppressTimer = null;
      this.pendingSuppression = null;

      if (suppressed && dwell) {
        this.pendingSuppression = true;
        this.suppressTimer = setTimeout(() => this.commitSuppression(true), SUCCESS_DWELL_MS);
        return;
      }

      this.commitSuppression(suppressed);
    }

    /**
     * Writes the suppression class and tells the Zendesk launcher. Split out of
     * applySuppression() so one body serves both the immediate path and the dwelled one.
     *
     * @param {boolean} suppressed - Whether the panel should now be hidden.
     */
    commitSuppression(suppressed) {
      this.suppressTimer = null;
      this.pendingSuppression = null;

      if (suppressed === this.classList.contains('sticky-atc-panel--suppressed')) return;

      // Keyboard hand-off before the panel goes visibility: hidden under a focused control.
      // On the add path the dwell moves this to ~SUCCESS_DWELL_MS, which races the cart
      // drawer's own trapFocus() (product-form.js schedules renderContents on a 700ms
      // timeout; cart-drawer.js defers open() by a further task). Both orders are safe: if
      // the hand-off wins, trapFocus() immediately takes focus on into the drawer; if
      // trapFocus() wins, this.contains(document.activeElement) is false and the hand-off is
      // skipped. It only does real work where the drawer is switched off ("After adding to
      // cart" → Do nothing), which is the one path with nothing else claiming focus.
      if (suppressed && this.contains(document.activeElement)) {
        const cartLink = document.querySelector('.header__icon[href$="/cart"]');
        if (cartLink) {
          cartLink.focus();
        } else {
          document.activeElement.blur();
        }
      }

      this.classList.toggle('sticky-atc-panel--suppressed', suppressed);
      // The Zendesk launcher offsets itself against the panel's height and reads the same
      // visibility classes (zendesk-identify.liquid) — without this it keeps floating above
      // a panel that is no longer there.
      window.dispatchEvent(new CustomEvent('sticky-atc-panel:change'));
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', StickyAtcPanel.handleScroll);
      // The panel is replaced wholesale on a variant change, so a pending dwell would fire
      // against a detached node — toggling a class nothing renders and dispatching a stray
      // sticky-atc-panel:change that jumps the Zendesk launcher.
      clearTimeout(this.suppressTimer);
    }

    bindEvents() {
      // Show the panel only once the add-to-cart form has scrolled above the viewport. The form
      // (not .product-info) is watched on all breakpoints — with "Stick product info on scroll"
      // enabled, .product-info stays in the viewport for the whole details area and the panel
      // would never appear on desktop.
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.target === this.productForm) {
            this.classList.toggle('sticky-atc-panel--out', entry.boundingClientRect.bottom > 0);
          }
          window.dispatchEvent(new CustomEvent('sticky-atc-panel:change'));
        });
      });

      if (this.productForm) observer.observe(this.productForm);

      window.addEventListener('scroll', StickyAtcPanel.handleScroll);

      this.addEventListener('click', this.handleScrollToClick.bind(this));
    }

    /**
     * Upgrades the panel's fragment link (configurator templates) to a smooth scroll.
     * The bare <a href="#..."> is the no-JS fallback, so this only intercepts.
     *
     * Smooth is load-bearing, not decorative: the panel hides itself from an
     * IntersectionObserver on the product form, and an instant jump moves the form from
     * below the viewport to above it without ever reporting an intersecting frame — the
     * observer stays false and the panel would sit there over the configurator the
     * visitor was just sent to. Under prefers-reduced-motion, where an instant jump is
     * the correct behaviour, the panel is hidden explicitly instead.
     */
    handleScrollToClick(event) {
      const trigger = event.target.closest('[data-scroll-to]');
      if (!trigger) return;

      const target = document.querySelector(trigger.getAttribute('href'));
      if (!target) return;

      event.preventDefault();

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });

      // Move focus to the target to maintain keyboard navigation flow
      if (!target.hasAttribute('tabindex')) {
        target.setAttribute('tabindex', '-1');
      }
      target.focus({ preventScroll: true });

      if (reduceMotion) {
        this.classList.add('sticky-atc-panel--out');
        window.dispatchEvent(new CustomEvent('sticky-atc-panel:change'));
      }
    }

    /**
     * Watches for a scroll to the bottom of the page
     */
    static handleScroll() {
      document.body.classList.toggle(
        'scrolled-to-bottom',
        window.scrollY + window.innerHeight + 100 > document.body.scrollHeight
      );
      window.dispatchEvent(new CustomEvent('sticky-atc-panel:change'));
    }
  }

  customElements.define('sticky-atc-panel', StickyAtcPanel);
}
